"use client";

import { FreeQuoteTrigger } from "@/components/freeQuoteDrawer/freeQuoteTrigger.component";
import { ADTLogo } from "@/components/icons/adtLogo.icon";
import { CustomerIcon } from "@/components/icons/customer.icon";
import { MenuIcon } from "@/components/icons/menu.icon";
import { PhoneIcon } from "@/components/icons/phone.icon";

import type { GetNavigationQueryResult } from "@/sanity.types";

import { PhoneNumber } from "../phoneNumber/phoneNumber.component";
import { SiteHeaderMainMenu } from "./siteHeaderMainMenu.component";
import { SiteHeaderMobileMenu } from "./siteHeaderMobileMenu.component";
import { SiteHeaderShopByMenu } from "./siteHeaderShopByMenu.component";

const openMobileMenu = () => {
  const modal = document.getElementById(
    "header-mobile-modal",
  ) as HTMLDialogElement | null;
  if (!modal) return;
  modal.showModal();
  document.body.style.overflow = "hidden";
};

export const SiteHeader = ({ shopByMenu, mainMenu }: SiteHeaderProps) => {
  return (
    <header
      className="relative z-40 flex flex-col overflow-x-clip bg-white"
      data-testid="main-header">
      <div className="container flex flex-col px-6 py-4 md:py-6 lg:gap-6">
        <section
          className="grid grid-cols-5 items-center justify-items-center lg:flex lg:justify-between lg:justify-items-stretch"
          data-testid="site-header-top-row">
          <div className="contents lg:flex lg:flex-1 lg:items-center lg:justify-start">
            <div className="hidden lg:flex">
              <SiteHeaderShopByMenu menu={shopByMenu} />
            </div>
            <button
              type="button"
              aria-label="Open mobile menu"
              aria-controls="header-mobile-modal"
              data-testid="site-header-mobile-menu-button"
              className="flex size-6 shrink-0 items-center justify-center leading-none lg:hidden [&_svg]:block"
              onClick={openMobileMenu}>
              <MenuIcon />
            </button>
          </div>
          <span
            className="flex size-6 shrink-0 items-center justify-center leading-none lg:hidden [&_svg]:block"
            data-testid="site-header-phone">
            <PhoneNumber showNumber={false} className="flex items-center">
              <PhoneIcon className="size-6.5" />
            </PhoneNumber>
          </span>
          <div className="items-left flex justify-center lg:flex-1">
            <a
              title="ADT"
              className="h-10 w-10 lg:h-12 lg:w-12"
              data-testid="site-header-adt-logo"
              href="/">
              <ADTLogo />
            </a>
          </div>
          <div className="contents lg:flex lg:flex-1 lg:items-center lg:justify-end lg:gap-6">
            <FreeQuoteTrigger
              label={mainMenu?.quoteTriggerLabel}
              ariaLabel={mainMenu?.quoteTriggerAriaLabel}
            />
            <span className="hidden shrink-0 items-center justify-center leading-none lg:flex [&_svg]:block">
              <PhoneNumber
                formatNumber
                showNumber={true}
                className="flex items-center"
                numberClassName="text-sm font-semibold">
                <PhoneIcon className="size-6.5" />
              </PhoneNumber>
            </span>
            <a
              title="Customer"
              href="/customer"
              className="flex size-6 shrink-0 items-center justify-center leading-none [&_svg]:block"
              data-testid="site-header-customer">
              <CustomerIcon className="size-6" />
            </a>
          </div>
        </section>
        <SiteHeaderMainMenu menu={mainMenu} />
      </div>
      <SiteHeaderMobileMenu shopByMenu={shopByMenu} mainMenu={mainMenu} />
    </header>
  );
};

export type SiteHeaderProps = {
  shopByMenu?: GetNavigationQueryResult;
  mainMenu?: GetNavigationQueryResult;
};
